/**
 * The v2 API speaks a different vocabulary than this UI was written against
 * (`id` vs `_id`, `foodType` vs `type`, `discountAmount` vs `discountValue`,
 * a flat `kitchenName`/`kitchenLogo` pair vs a nested `sellerId` object…).
 *
 * Rather than rename fields across ~40 components, every API module below
 * funnels its responses through the mappers here, so components keep reading
 * the shape they already render while the wire format stays whatever the
 * backend says it is. One place to update when the backend moves again.
 */

/* ── Food ─────────────────────────────────────────────────────────────── */

/** The API's food types. "offer" is a client-side view over "has a
 *  discount", not a server food type. */
export const FOOD_TYPE = {
  READY_TO_EAT: "ready-to-eat",
  PRE_ORDER: "pre-order",
  E_SHOP: "e-shop",
};
export const FOOD_TYPES = Object.values(FOOD_TYPE);

export const FOOD_TYPE_LABELS = {
  [FOOD_TYPE.READY_TO_EAT]: "Ready to Eat",
  [FOOD_TYPE.PRE_ORDER]: "Pre-order",
  [FOOD_TYPE.E_SHOP]: "E-Shop",
};

/** Label for a food type, falling back to the raw value for anything unknown.
 *  Pre-rename values (e.g. in a cart saved before the rename) still label. */
export const foodTypeLabel = (type) => FOOD_TYPE_LABELS[normalizeFoodTypeParam(type)] ?? type;

/** Pre-rename values still found in old bookmarked links. */
const LEGACY_FOOD_TYPES = { instant: FOOD_TYPE.READY_TO_EAT, readymade: FOOD_TYPE.E_SHOP };

/** A food type from a URL, with pre-rename values mapped onto the API's names
 *  (case-insensitively, since old links were typed by hand too). */
export const normalizeFoodTypeParam = (value) => {
  if (typeof value !== "string") return value;
  const lower = value.toLowerCase();
  return LEGACY_FOOD_TYPES[lower] ?? (FOOD_TYPES.includes(lower) ? lower : value);
};

/**
 * Milliseconds until a ready-to-eat item's active window closes, from the
 * API's own `expiresAt` (publishedAt + activeHours, restarted on re-publish).
 * Null when the item never expires; 0 once it has (or the API says so).
 */
export const foodTimeLeftMs = (food, now = Date.now()) => {
  if (food?.isExpired) return 0;
  if (!food?.expiresAt) return null;
  return Math.max(0, Date.parse(food.expiresAt) - now);
};

export const normalizeFood = (food, fallbackKitchen = null) => {
  if (!food) return null;

  const kitchenName = food.kitchenName ?? fallbackKitchen?.name ?? null;
  const kitchenLogo = food.kitchenLogo ?? fallbackKitchen?.kitchenLogo ?? null;
  const chefId = food.chefId ?? fallbackKitchen?._id ?? null;

  return {
    ...food,
    _id: food.id,
    // Review-maintained rating cache, same shape the kitchen DTO carries.
    reviews: ratingSummary(food.ratingCounts),
    modeRatingLabel: reviewRatingLabel(food.modeRating),
    // `discountedPrice` is computed server-side against the effective price;
    // `discountValue` is kept because several cards render the raw number.
    discountValue: food.discountAmount ?? 0,
    discountedPrice: food.discountedPrice ?? food.price,
    type: food.foodType,
    foodType: food.foodType,
    // Cart quantity guards read `foodStock`; the detail page reads `quantity`.
    foodStock: food.stock,
    quantity: food.stock,
    // The ready-to-eat countdown reads `expiresAt` / `isExpired` straight from
    // the API (spread above) — see foodTimeLeftMs.
    waitingTime: food.prepTime ? `${food.prepTime} min` : null,
    sellerID: chefId,
    sellerId: chefId
      ? { _id: chefId, name: kitchenName, kitchenName, kitchenLogo }
      : null,
    kitchenIsOpen: food.kitchenIsOpen ?? true,
    location:
      food.latitude != null && food.longitude != null
        ? { lat: food.latitude, long: food.longitude, lng: food.longitude }
        : null,
  };
};

export const normalizeFoods = (foods, fallbackKitchen = null) =>
  (Array.isArray(foods) ? foods : []).map((f) =>
    normalizeFood(f, fallbackKitchen),
  );

/** True when a food carries a live discount — the client-side "offer" filter. */
export const isOfferFood = (food) =>
  !!food?.discountType && Number(food?.discountValue) > 0;

/* ── Kitchen (chef) ───────────────────────────────────────────────────── */

const RATING_SCORE = { not_tasty: 1, tasty: 3, delicious: 5 };

/** Collapses the three-bucket rating cache into the 0–5 star the UI shows. */
export const ratingSummary = (counts) => {
  const c = counts || {};
  const total =
    (c.notTasty || 0) + (c.tasty || 0) + (c.delicious || 0);
  if (!total) return { rating: 0, count: 0 };
  const score =
    (c.notTasty || 0) * RATING_SCORE.not_tasty +
    (c.tasty || 0) * RATING_SCORE.tasty +
    (c.delicious || 0) * RATING_SCORE.delicious;
  return { rating: Number((score / total).toFixed(1)), count: total };
};

export const normalizeKitchen = (chef) => {
  if (!chef) return null;
  const reviews = ratingSummary(chef.ratingCounts);
  const settings = chef.kitchenSettings || null;

  return {
    ...chef,
    _id: chef.id,
    // The UI calls the kitchen "name" and the person "chefName".
    name: chef.kitchenName,
    kitchenName: chef.kitchenName,
    kitchenLogo: chef.kitchenLogo,
    chefDescription: chef.description,
    kitchenDescription: chef.kitchenDescription,
    specialty: chef.specialities || [],
    // Legacy shape: `restrictions` had no v2 counterpart, so it stays empty
    // rather than silently reusing an unrelated field.
    restrictions: [],
    address: {
      text: chef.address,
      lat: chef.latitude,
      lng: chef.longitude,
      long: chef.longitude,
    },
    reviews,
    sellerProfile: { user: { name: chef.chefName, avatar: chef.avatar } },
    deliveryCharge: settings?.baseDeliveryCharge ?? 0,
    kitchenSettings: settings,
  };
};

export const normalizeKitchens = (chefs) =>
  (Array.isArray(chefs) ? chefs : []).map(normalizeKitchen);

/* ── Recipe ───────────────────────────────────────────────────────────── */

const minutesLabel = (minutes) =>
  minutes == null ? null : minutes >= 60
    ? `${Math.round((minutes / 60) * 10) / 10} hr`
    : `${minutes} min`;

export const normalizeRecipe = (recipe, kitchenById = {}) => {
  if (!recipe) return null;
  const kitchen = recipe.chefId ? kitchenById[recipe.chefId] : null;
  // The paywall withholds ingredients and steps entirely — never partially.
  const steps = [...(recipe.steps || [])].sort(
    (a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0),
  );

  return {
    ...recipe,
    _id: recipe.id,
    // Cards read `title`, the detail page reads `name`.
    title: recipe.name,
    images: recipe.images || [],
    kitchenId: kitchen
      ? {
          _id: kitchen._id,
          name: kitchen.name,
          kitchenLogo: kitchen.kitchenLogo,
          address: kitchen.address,
        }
      : recipe.chefId
        ? { _id: recipe.chefId, name: null, kitchenLogo: null }
        : null,
    discountValue: recipe.discountAmount ?? 0,
    // Legacy aliases the recipe detail page renders.
    isPaid: Number(recipe.price) > 0,
    finalPrice: recipe.discountedPrice ?? recipe.price,
    difficultyLevel: recipe.difficulty,
    cookingTime: minutesLabel(recipe.cookTime),
    preparationTime: minutesLabel(recipe.prepTime),
    serves: recipe.servings,
    // v2 keeps video per step; the first one stands in for the recipe video.
    videoUrl: steps.find((s) => s.videoUrl)?.videoUrl || null,
    ingredients: (recipe.ingredients || [])
      .slice()
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((item) => ({ ...item, qtyUnit: item.unit })),
    // There are no nested sub-steps in v2 — every step is top level.
    steps: steps.map((step) => ({ ...step, _id: `step-${step.stepOrder}` })),
  };
};

export const normalizeRecipes = (recipes, kitchenById = {}) =>
  (Array.isArray(recipes) ? recipes : []).map((r) =>
    normalizeRecipe(r, kitchenById),
  );

/* ── Category / Cuisine ───────────────────────────────────────────────── */

export const normalizeCategory = (category) =>
  category ? { ...category, _id: category.id } : null;

export const normalizeCategories = (categories) =>
  (Array.isArray(categories) ? categories : []).map(normalizeCategory);

/* ── Address ──────────────────────────────────────────────────────────── */

export const normalizeAddress = (address) =>
  address ? { ...address, _id: address.id } : null;

export const normalizeAddresses = (addresses) =>
  (Array.isArray(addresses) ? addresses : []).map(normalizeAddress);

/* ── Coupon ───────────────────────────────────────────────────────────── */

export const normalizeCoupon = (coupon) => {
  if (!coupon) return null;
  return {
    ...coupon,
    _id: coupon.id,
    type: coupon.discountType, // "percentage" | "fixed"
    value: coupon.discountValue,
    minOrder: coupon.minOrderValue ?? 0,
  };
};

export const normalizeCoupons = (coupons) =>
  (Array.isArray(coupons) ? coupons : []).map(normalizeCoupon);

/* ── Order ────────────────────────────────────────────────────────────── */

/**
 * v2 statuses are SCREAMING_CASE and finer-grained than the four buckets the
 * order screens colour-code. Lower-casing plus this alias table keeps every
 * existing STATUS_CFG lookup resolving.
 */
export const ORDER_STATUS_ALIAS = {
  pending: "pending",
  confirmed: "confirmed",
  preparing: "processing",
  ready: "processing",
  out_for_delivery: "processing",
  delivered: "delivered",
  cancelled: "cancelled",
};

export const orderStatusLabel = (status) => {
  const raw = String(status || "").toLowerCase();
  return (
    {
      pending: "Pending",
      confirmed: "Confirmed",
      preparing: "Preparing",
      ready: "Ready",
      out_for_delivery: "Out for delivery",
      delivered: "Delivered",
      cancelled: "Cancelled",
    }[raw] || "Pending"
  );
};

export const normalizeOrder = (order) => {
  if (!order) return null;
  const rawStatus = String(order.status || "").toLowerCase();
  const paymentStatus =
    order.paymentMethod === "cod" && order.paymentStatus === "pending"
      ? "cod_pending"
      : order.paymentStatus;

  return {
    ...order,
    _id: order.id,
    status: ORDER_STATUS_ALIAS[rawStatus] || rawStatus,
    rawStatus,
    statusLabel: orderStatusLabel(rawStatus),
    paymentStatus,
    subTotal: order.subtotal,
    discount: order.discountAmount ?? 0,
    coupon: order.couponCode ?? "",
    additionalInfo: order.notes ?? "",
    items: (order.items || []).map((item, index) => ({
      ...item,
      _id: item.foodId || `item-${index}`,
      // v2 snapshots name/price/quantity only — there is no image on an order
      // item, so cards fall back to their placeholder tile.
      image: item.image || null,
      lineTotal: item.lineTotal ?? item.price * item.quantity,
    })),
    sellerID: order.chefId,
    sellerId: order.chefId ? { _id: order.chefId } : null,
  };
};

export const normalizeOrders = (orders) =>
  (Array.isArray(orders) ? orders : []).map(normalizeOrder);

/* ── Review ───────────────────────────────────────────────────────────── */

/** The API's rating codes vs. the words the review widgets display. */
export const REVIEW_RATINGS = [
  { code: "not_tasty", label: "Not tasty", color: "#EF4444" },
  { code: "tasty", label: "Tasty", color: "#F59E0B" },
  { code: "delicious", label: "Delicious", color: "#22C55E" },
];

export const reviewRatingLabel = (code) =>
  REVIEW_RATINGS.find((r) => r.code === code)?.label || "";

export const normalizeReview = (review) => {
  if (!review) return null;
  return {
    ...review,
    _id: review.id,
    ratingLabel: reviewRatingLabel(review.rating),
    order: review.orderId,
    user: { name: review.reviewerName || "Customer", avatar: null },
  };
};

export const normalizeReviews = (reviews) =>
  (Array.isArray(reviews) ? reviews : []).map(normalizeReview);

/* ── Wish order ───────────────────────────────────────────────────────── */

/** v2: PENDING | NEGOTIATING | ACCEPTED | REJECTED | EXPIRED. */
export const WISH_STATUS_ALIAS = {
  pending: "pending",
  negotiating: "pending",
  accepted: "accepted",
  rejected: "rejected",
  expired: "cancelled",
};

/** The API's own wish statuses — what the list endpoint filters on. */
export const WISH_STATUSES = [
  { code: "PENDING", label: "Pending" },
  { code: "NEGOTIATING", label: "Negotiating" },
  { code: "ACCEPTED", label: "Accepted" },
  { code: "REJECTED", label: "Rejected" },
  { code: "EXPIRED", label: "Expired" },
];

export const wishStatusLabel = (rawStatus) =>
  WISH_STATUSES.find((s) => s.code === String(rawStatus || "").toUpperCase())
    ?.label || "Pending";

export const normalizeWishOrder = (wish) => {
  if (!wish) return null;
  const rawStatus = String(wish.status || "").toLowerCase();
  // A wish that has produced an order is done, whatever its own status says.
  const status = wish.orderId
    ? "completed"
    : WISH_STATUS_ALIAS[rawStatus] || rawStatus;

  return {
    ...wish,
    _id: wish.id,
    status,
    rawStatus,
    // "Ordered" is derived from orderId — the API has no such status.
    statusLabel: wish.orderId ? "Ordered" : wishStatusLabel(rawStatus),
    photos: wish.referencePhotos || [],
    videoLink: wish.referenceLink || "",
    additionalInfo: wish.notes || "",
    // v2 stores ingredients as a list; the old form rendered a single blob.
    ingredients: (wish.ingredients || []).join(", "),
    ingredientList: wish.ingredients || [],
    desiredDate: wish.desiredFor,
    desiredTime: wish.timeSlot,
    sellerId: wish.chefId
      ? { _id: wish.chefId, name: wish.kitchenName, kitchenName: wish.kitchenName }
      : null,
    sellerName: wish.kitchenName,
    // Offer log, oldest first — what the pricing screen renders as a thread.
    negotiations: [...(wish.offers || [])]
      .map((offer) => ({
        _id: offer.id,
        actor: offer.by === "seller" ? "SELLER" : "BUYER",
        price: offer.amount,
        createdAt: offer.at,
      }))
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    agreedPrice: wish.agreedPrice,
    orderId: wish.orderId,
  };
};

export const normalizeWishOrders = (wishes) =>
  (Array.isArray(wishes) ? wishes : []).map(normalizeWishOrder);

/* ── Notification ─────────────────────────────────────────────────────── */

export const normalizeNotification = (notification) => {
  if (!notification) return null;
  return {
    ...notification,
    _id: notification.id,
    data: notification.metadata || {},
  };
};

export const normalizeNotifications = (notifications) =>
  (Array.isArray(notifications) ? notifications : []).map(
    normalizeNotification,
  );

/* ── User ─────────────────────────────────────────────────────────────── */

export const normalizeUser = (user) => {
  if (!user) return null;
  return {
    ...user,
    _id: user.id,
    // Phone is the login identity in v2; the old UI called it mobileNumber.
    mobileNumber: user.phone,
  };
};
